import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@betty/i18n';
import { formatAmount } from '@betty/toolkit/utils';
import {
  Box,
  Text,
  Icon,
  Button,
  AmountInput,
  KeyboardSpacer,
  validateAmount,
} from '@betty/moonlight';
import { getAmountLimit } from '../../components/Deposit/Amounts/Amounts.utils';
import { DepositAmountProps } from './DepositAmount.types';

function DepositAmount({
  amount,
  minAmount,
  maxAmount,
  limits,
  onSelect,
  close,
}: DepositAmountProps) {
  const { t } = useTranslation('wallet');
  const [value, setValue] = useState(amount !== undefined ? `${amount}` : '');
  const [error, setError] = useState('');
  const limit = useMemo(() => getAmountLimit(limits), [limits]);

  const handleSelect = useCallback(() => {
    if (error) {
      return;
    }

    if (validateAmount(value)) {
      onSelect(Number(value));
      close();

      return;
    }

    setError(t('depositInvalidAmount'));
  }, [value, error, onSelect, close, t]);

  useEffect(() => {
    if (!value) {
      setError('');

      return;
    }

    const parsedValue = Number(value);

    if (parsedValue < minAmount) {
      setError(
        t('depositMinAmountError', {
          amount: formatAmount(minAmount),
        }),
      );

      return;
    }

    if (!limit && parsedValue > maxAmount) {
      setError(
        t('depositMaxAmountError', {
          amount: formatAmount(maxAmount),
        }),
      );

      return;
    }

    if (limit) {
      const diff = (limit.amount ?? 0) - limit.accumulatedAmount;
      const limitAmount = Math.min(maxAmount, diff);

      if (parsedValue > limitAmount) {
        setError(t('depositLimitExceeded'));

        return;
      }
    }

    setError('');
  }, [value, minAmount, maxAmount, limit, t]);

  return (
    <Box gap="space-100">
      <Text
        variant="body-l-semi"
        locNS="wallet"
        locKey="depositAmountTitle"
        color="darkSurfacePrimary"
      />
      <Text
        variant="body-s"
        gradient="darkSurfaceAccent"
        paddingBottom="space-300"
      >
        {t('depositLimitTitle', {
          minAmount: formatAmount(minAmount),
          maxAmount: formatAmount(
            limit?.amount
              ? Math.min(limit.amount - limit.accumulatedAmount, maxAmount)
              : maxAmount,
          ),
        })}
      </Text>
      <AmountInput
        value={value}
        error={error}
        placeholderNS="wallet"
        placeholderKey="depositAmount"
        onChangeText={setValue}
      />
      <Box
        flexDirection="row"
        alignItems="center"
        marginBottom="space-100"
        height="space-600"
        gap="space-100"
        opacity={Number(!!error)}
      >
        <Icon
          name="info"
          width="space-400"
          height="space-400"
          color="darkSurfaceError"
        />
        <Text variant="body-s" color="darkSurfaceError">
          {error}
        </Text>
      </Box>
      <Box flexDirection="row" alignSelf="flex-end" gap="space-200">
        <Button
          locKey="cancel"
          locNS="common"
          variant="secondary"
          minWidth={100}
          onPress={close}
        />
        <Button
          locKey="done"
          locNS="common"
          minWidth={100}
          disabled={!!error}
          onPress={handleSelect}
        />
      </Box>
      <KeyboardSpacer />
    </Box>
  );
}

export default memo(DepositAmount);
